import {
  DownloadIcon,
  PlusIcon,
  ShoppingCartIcon,
} from '@phosphor-icons/react';
import { useCallback, useMemo, useState } from 'react';

import PageHeader from '@/components/blocks/page/header';
import MetricsDisplay from '@/components/blocks/shared/metrics-display';
import OrderCreateSheet from '@/components/blocks/sheets/order-create';
import { CreateGuard, ViewGuard } from '@/components/guards/permission-guards';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import Container from '@/components/ui/container';
import { DataTable } from '@/components/ui/data-table';
import Row from '@/components/ui/row';
import { showToast } from '@/components/ui/toast';
import { useGlobalState } from '@/contexts/GlobalStateContext';
import { useConsolidatedFetch } from '@/hooks/use-consolidated-fetch';
import { useEnhancedApi } from '@/hooks/use-enhanced-api';
import {
  ActionConfig,
  DataTableColumn,
  PaginationMeta,
} from '@/types/datatable';
import {
  OrderData,
  OrderExportParams,
  OrderFilterState,
  OrderMetrics,
  OrderPaginationState,
} from '@/types/orders';
import { capitalizeFirstLetter, formatCurrency, formatDate } from '@/utils';
import { ACTIONS, RESOURCES, usePermissions } from '@/utils/permissions';

const Orders = () => {
  const api = useEnhancedApi();
  const { showSheet } = useGlobalState();
  const permissions = usePermissions();

  const [orders, setOrders] = useState<OrderData[]>([]);
  const [metrics, setMetrics] = useState<OrderMetrics | null>(null);
  const [pagination, setPagination] = useState<PaginationMeta | undefined>(
    undefined
  );
  const [isExporting, setIsExporting] = useState(false);

  // Internal state for filters and pagination (no URL sync)
  const [filters, setFilters] = useState<OrderFilterState>({
    search: '',
    status: '',
    paymentStatus: '',
  });

  const [paginationState, setPaginationState] = useState<OrderPaginationState>({
    page: 1,
    limit: 10,
  });

  // Consolidated data fetching to prevent duplicate requests
  const { loading, refetch: fetchAllData } = useConsolidatedFetch({
    operations: [
      {
        key: 'orders',
        fetcher: async () => {
          const queryParams: Record<string, string> = {
            page: paginationState.page.toString(),
            limit: paginationState.limit.toString(),
          };

          if (filters.search) queryParams.search = filters.search;
          if (filters.status) queryParams.status = filters.status;
          if (filters.paymentStatus)
            queryParams.paymentStatus = filters.paymentStatus;

          return await api.orders.getAll(queryParams);
        },
        setter: response => {
          if (response.success && response.data) {
            const data: any = response.data as any;
            // Support multiple response shapes
            if (Array.isArray(data.orders)) {
              setOrders(data.orders);
              const meta = data.pagination || {};
              setPagination({
                page: meta.page || paginationState.page,
                limit: meta.limit || paginationState.limit,
                total: meta.totalCount || meta.totalDocs || 0,
                totalPages: meta.totalPages || 0,
                hasNext: meta.hasNextPage || false,
                hasPrev: meta.hasPrevPage || false,
              });
            } else if (data.orders?.docs) {
              const list = data.orders;
              setOrders(list.docs || []);
              setPagination({
                page: list.page || paginationState.page,
                limit: list.limit || paginationState.limit,
                total: list.totalDocs || list.total || 0,
                totalPages: list.totalPages || 0,
                hasNext: list.hasNextPage || false,
                hasPrev: list.hasPrevPage || false,
              });
            } else if (Array.isArray(data.docs)) {
              setOrders(data.docs);
              setPagination({
                page: data.page || paginationState.page,
                limit: data.limit || paginationState.limit,
                total: data.totalDocs || data.total || 0,
                totalPages: data.totalPages || 0,
                hasNext: data.hasNextPage || false,
                hasPrev: data.hasPrevPage || false,
              });
            } else {
              // Fallback
              setOrders([]);
              setPagination(undefined);
            }
          } else {
            setPagination(undefined);
          }
        },
        onError: error => {
          showToast(`Error fetching orders: ${error}`, false);
          setPagination(undefined);
        },
      },
      {
        key: 'metrics',
        fetcher: async () => {
          return await api.orders.getMetrics();
        },
        setter: response => {
          if (response.success) {
            setMetrics(response.data);
          }
        },
        onError: error => {
          showToast(`Error fetching metrics: ${error}`, false);
        },
      },
    ],
    dependencies: [
      paginationState.page,
      paginationState.limit,
      filters.search,
      filters.status,
      filters.paymentStatus,
    ],
  });

  // Order columns configuration
  const orderColumns: DataTableColumn<OrderData>[] = useMemo(
    () => [
      {
        id: 'orderNumber',
        header: 'Order Number',
        accessor: 'orderNumber',
        cell: ({ row }) => (
          <div className='font-medium text-gray-90'>{row.original.orderId}</div>
        ),
        sortable: true,
      },
      {
        id: 'customer',
        header: 'Customer',
        accessor: 'customer.contactName',
        cell: ({ row }) => (
          <div>
            <div className='font-medium text-gray-90'>
              {row.original.customer?.contactName || 'N/A'}
            </div>
            {row.original.customer?.businessName && (
              <div className='text-sm text-gray-50'>
                {row.original.customer.businessName}
              </div>
            )}
          </div>
        ),
        sortable: true,
      },
      {
        id: 'totalAmount',
        header: 'Total Amount',
        accessor: 'totalAmount',
        cell: ({ row }) => (
          <span className='font-medium'>
            {formatCurrency(Number(row.original.totalAmount), 'NGN')}
          </span>
        ),
        sortable: true,
      },
      {
        id: 'status',
        header: 'Order Status',
        accessor: 'status',
        cell: ({ row }) => {
          const status = row.original.status;
          const statusColor = {
            pending: 'warning',
            confirmed: 'brand',
            processing: 'brand',
            shipped: 'success',
            delivered: 'success',
            cancelled: 'destructive',
          }[status] as 'warning' | 'brand' | 'success' | 'destructive';

          return (
            <Badge
              variant='outline'
              className={`text-${statusColor}-600 border-${statusColor}-200`}
            >
              {capitalizeFirstLetter(status)}
            </Badge>
          );
        },
        sortable: true,
      },
      {
        id: 'paymentStatus',
        header: 'Payment',
        accessor: 'paymentStatus',
        cell: ({ row }) => {
          const paymentStatus = row.original.paymentStatus || 'pending';
          const statusColor = {
            pending: 'warning',
            paid: 'success',
            failed: 'destructive',
            refunded: 'destructive',
          }[paymentStatus] as 'warning' | 'success' | 'destructive';

          return (
            <Badge
              variant='outline'
              className={`text-${statusColor}-600 border-${statusColor}-200`}
            >
              {capitalizeFirstLetter(paymentStatus)}
            </Badge>
          );
        },
        sortable: true,
      },
      {
        id: 'approvalStatus',
        header: 'Approval Status',
        accessor: 'approvalStatus',
        cell: ({ row }) => {
          const approvalStatus = row.original.approvalStatus || 'pending';
          const statusColor = {
            pending: 'warning',
            approved: 'success',
            rejected: 'destructive',
          }[approvalStatus] as 'warning' | 'success' | 'destructive';

          return (
            <Badge
              variant='outline'
              className={`text-${statusColor}-600 border-${statusColor}-200`}
            >
              {capitalizeFirstLetter(approvalStatus)}
            </Badge>
          );
        },
        sortable: true,
      },
      {
        id: 'source',
        header: 'Source',
        accessor: 'source',
        cell: ({ row }) => (
          <Badge variant='secondary'>
            {capitalizeFirstLetter(row.original.source || 'unknown')}
          </Badge>
        ),
        sortable: true,
      },
      {
        id: 'createdAt',
        header: 'Order Date',
        accessor: 'createdAt',
        cell: ({ row }) => (
          <span>
            {formatDate(row.original.createdAt || new Date().toISOString())}
          </span>
        ),
        sortable: true,
      },
    ],
    []
  );

  // Get unique statuses for filtering
  const uniqueStatuses = useMemo(
    () =>
      Array.from(new Set(orders.map(order => order.status).filter(Boolean))),
    [orders]
  );

  const uniquePaymentStatuses = useMemo(
    () =>
      Array.from(
        new Set(orders.map(order => order.paymentStatus).filter(Boolean))
      ),
    [orders]
  );

  // Handle search changes
  const handleSearchChange = useCallback((search: string) => {
    setFilters(prev => ({ ...prev, search }));
    setPaginationState(prev => ({ ...prev, page: 1 })); // Reset to page 1 when searching
  }, []);

  // Handle filter changes
  const handleFiltersChange = useCallback(
    (newFilters: Record<string, string>) => {
      setFilters(prev => ({
        ...prev,
        status: newFilters.status || '',
        paymentStatus: newFilters.paymentStatus || '',
      }));
      setPaginationState(prev => ({ ...prev, page: 1 })); // Reset to page 1 when filtering
    },
    []
  );

  // Handle pagination changes
  const handlePaginationChange = useCallback(
    (newPagination: { pageIndex: number; pageSize: number }) => {
      setPaginationState({
        page: newPagination.pageIndex + 1, // Convert 0-based to 1-based
        limit: newPagination.pageSize,
      });
    },
    []
  );

  // Row actions for orders
  const orderRowActions = useMemo<ActionConfig<OrderData>[]>(() => {
    const actions: ActionConfig<OrderData>[] = [];

    // View action - requires read permission
    if (permissions.can(RESOURCES.SALES_ORDERS, ACTIONS.READ)) {
      actions.push({
        key: 'view',
        label: 'View Details',
        onClick: (order: OrderData) => {
          window.location.href = `/orders/${order._id}`;
        },
      });
    }

    return actions;
  }, [permissions]);

  // Filter configurations
  const orderFilters = useMemo(
    () => [
      {
        key: 'status',
        label: 'Order Status',
        type: 'select' as const,
        options: [
          { value: '', label: 'All Statuses' },
          ...uniqueStatuses.map(status => ({
            value: status || '',
            label: capitalizeFirstLetter(status || ''),
          })),
        ],
        placeholder: 'Filter by status',
      },
      {
        key: 'paymentStatus',
        label: 'Payment Status',
        type: 'select' as const,
        options: [
          { value: '', label: 'All Payment Status' },
          ...uniquePaymentStatuses.map(status => ({
            value: status || '',
            label: capitalizeFirstLetter(status || ''),
          })),
        ],
        placeholder: 'Filter by payment status',
      },
    ],
    [uniqueStatuses, uniquePaymentStatuses]
  );

  // Create order sheet handler
  const orderCreateSheet = useCallback(() => {
    showSheet({
      id: 'create-order',
      type: 'create-order',
      title: 'Create Sales Order',
      content: <OrderCreateSheet onOrderCreated={fetchAllData} />,
    });
  }, [showSheet, fetchAllData]);

  // Export orders handler
  const handleExport = useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      const exportParams: OrderExportParams = {};

      // Add current filters to export
      if (filters.search) exportParams.search = filters.search;
      if (filters.status) exportParams.status = filters.status;
      if (filters.paymentStatus)
        exportParams.paymentStatus = filters.paymentStatus;

      const response = await api.orders.export(exportParams as any);

      if (response.success && response.data) {
        const disposition = response.headers?.['content-disposition'] || '';
        let filename = `orders_${new Date().toISOString().replace(/[-:]/g, '').split('T')[0]}.xlsx`;
        const match = disposition.match(/filename="?([^";]+)"?/i);
        if (match && match[1]) {
          filename = match[1];
        }

        const blob = response.data as Blob;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        showToast('Orders exported successfully!', true);
      } else {
        showToast(`Export failed: ${response.message}`, false);
      }
    } catch {
      showToast('Export failed. Please try again.', false);
    } finally {
      setIsExporting(false);
    }
  }, [api.orders, filters, isExporting]);

  // Prepare metrics for MetricsDisplay component
  const metricsData = useMemo(() => {
    if (!metrics) return [];

    return [
      {
        id: 'totalOrders',
        title: 'Total Orders',
        value: metrics.totalOrders,
        color: 'brand' as const,
      },
      {
        id: 'totalOngoingOrders',
        title: 'Total Ongoing Orders',
        value: metrics.totalOngoingOrders,
        color: 'warning' as const,
      },
      {
        id: 'totalPaidOrders',
        title: 'Total Paid Orders',
        value: metrics.totalPaidOrders,
        color: 'success' as const,
      },
      {
        id: 'totalUnpaidOrders',
        title: 'Total Unpaid Orders',
        value: metrics.totalUnpaidOrders,
        color: 'lime' as const,
      },
    ];
  }, [metrics]);

  return (
    <ViewGuard
      resource={RESOURCES.SALES_ORDERS}
      fallback={
        <Container className='space-y-6'>
          <div className='text-center py-12'>
            <ShoppingCartIcon className='h-12 w-12 text-gray-40 mx-auto mb-4' />
            <h3 className='text-lg font-semibold text-gray-90 mb-2'>
              Access Denied
            </h3>
            <p className='text-gray-60'>
              You don&apos;t have permission to view sales orders. Contact your
              administrator for access.
            </p>
          </div>
        </Container>
      }
    >
      <Container className='space-y-6'>
        <Row>
          <PageHeader
            title='Sales Orders'
            subtitle='Manage all customer orders and track their progress'
            showExport={permissions.can(RESOURCES.SALES_ORDERS, ACTIONS.READ)}
            actions={
              <>
                <ViewGuard resource={RESOURCES.SALES_ORDERS}>
                  <Button
                    hierarchy='outlined'
                    onClick={handleExport}
                    disabled={isExporting}
                  >
                    <DownloadIcon className='h-4 w-4' />
                    {isExporting ? 'Exporting...' : 'Export'}
                  </Button>
                </ViewGuard>
                <CreateGuard resource={RESOURCES.SALES_ORDERS}>
                  <Button onClick={orderCreateSheet}>
                    <PlusIcon className='h-4 w-4' />
                    Create Sales Order
                  </Button>
                </CreateGuard>
              </>
            }
          />
        </Row>

        <MetricsDisplay metrics={metricsData} />

        <Row>
          <DataTable
            data={orders}
            columns={orderColumns}
            loading={loading}
            pagination={pagination}
            onPaginationChange={handlePaginationChange}
            globalFilter={filters.search}
            onGlobalFilterChange={handleSearchChange}
            filters={{
              status: filters.status,
              paymentStatus: filters.paymentStatus,
            }}
            onFiltersChange={handleFiltersChange}
            rowActions={orderRowActions}
            enableUrlSync={false}
            header={{
              title: 'Orders',
              subtitle: 'Track and manage customer orders',
              showSearch: true,
              searchConfig: {
                placeholder: 'Search orders by number or customer...',
              },
              showFilters: true,
              filters: orderFilters,
            }}
            footer={{
              showPagination: true,
              showRecordCount: true,
            }}
            enableSorting={true}
            enableColumnVisibility={false}
            enablePagination={true}
            emptyConfig={{
              title: 'No Orders Found',
              description: 'Get started by creating your first sales order.',
              icon: <ShoppingCartIcon className='h-12 w-12 text-gray-40' />,
            }}
          />
        </Row>
      </Container>
    </ViewGuard>
  );
};

export default Orders;
